import { useState, useContext, useEffect } from "react";

import DataTable from "@shared-ui-components/DataTable";

import LocationContext from "@context/location/locationContext";
import {
  CogsRevCenter,
  CogsStore,
} from "@shared-ui-components/DataTable/types";

import { getCustomExtraPath } from "@src/pages/Dashboard/RevenueDashBoard/TotalSales/helper";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { getApiResponse } from "@src/utils/service";
import { SORTKEYMAP } from "@src/constants/DataTable";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { message } from "@src/constants";
import { columns, defaultOptions, filterOptions, refineData } from "./helper";

const CogsLocationBreakdownTable = ({
  pagination,
}: {
  pagination: boolean;
}) => {
  const [type, setType] = useState("");
  const [dyColumns, setDyColumns] = useState(columns);
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowsData] = useState<CogsStore[] | CogsRevCenter[]>([]);
  const [typeOptions, setTypeOptions] = useState(defaultOptions);

  const [pageNumber, setPageNumber] = useState(0);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState({
    tableName: "cogs",
    label: "COGS",
    order: "desc",
  });
  const [{ locationState }] = useContext(LocationContext);

  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const filterType = filterOptions.find((el) => el.label === type)?.value;

  const filters = {
    property,
    store,
    rvc: "",
    date,
    filterBy: filterType || "",
  };

  const filter = getCustomExtraPath(filters);

  const url = `cogs-breakdown${filter}&sort_by=${sortBy.tableName}&sort_order=${
    sortBy.order === "desc" ? "desc" : "asc"
  }&page=${pageNumber + 1}`;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage - 1);
  };

  const setLocalFilter = (changedtype: string) => {
    localStorage.setItem(
      "CogsFilter",
      JSON.stringify({ filtertype: changedtype })
    );
  };

  const handleDyColumns = (changedType: string) => {
    setDyColumns((prev) => {
      const newColumns = prev.filter(
        (column) => column.label !== "Rev Center" && column.label !== "Store"
      );
      return [
        {
          id: "name",
          label:
            changedType.toLowerCase() === "by store" ? "Store" : "Rev Center",
          minWidth: 102,
        },
        ...newColumns,
      ];
    });
  };

  const localCogsFilter = localStorage.getItem("CogsFilter");

  const handleTypeChange = (changedType: string) => {
    setRowsData([]);
    setIsLoading(true);
    setPageNumber(0);
    handleDyColumns(changedType);
    setType(changedType);
    setLocalFilter(changedType);
  };

  const sortHandler = (sortkey: string) => {
    const sortKeyObject = SORTKEYMAP.find(
      (el) => el.tableName === sortkey.toLowerCase()
    );
    setSortBy((prev) => {
      return {
        tableName: sortKeyObject?.label || "name",
        label: sortkey,
        order: prev.order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handleNavigate = () => {
    navigate("/cogs-dashboard/cogs-breakdown");
  };

  const handleLocalFilters = () => {
    if (localCogsFilter !== null && JSON.parse(localCogsFilter).filtertype) {
      const { filtertype } = JSON.parse(localCogsFilter);

      setType(filtertype);
      handleDyColumns(filtertype);
      if (!store) {
        if (property === "002" && filtertype === "By Rev Center") {
          setType("By Store");
        }
        if (property === "002") {
          setTypeOptions([{ label: "By Store", value: "store" }]);
        } else {
          setTypeOptions([
            { label: "By Store", value: "store" },
            { label: "By Rev Center", value: "rvc" },
          ]);
        }
      } else {
        if (property === "002") {
          setTypeOptions([{ label: "By Store", value: "store" }]);
          setLocalFilter("By Store");
          return;
        }
        setTypeOptions([{ label: "By Rev Center", value: "rvc" }]);
        setLocalFilter("By Rev Center");
      }
    } else {
      if (!store) {
        if (property === "002") {
          setTypeOptions([{ label: "By Store", value: "store" }]);
        } else {
          setTypeOptions([
            { label: "By Store", value: "store" },
            { label: "By Rev Center", value: "rvc" },
          ]);
        }
        setType("By Store");
      } else if (store && property !== "002") {
        setTypeOptions([{ label: "By Rev Center", value: "rvc" }]);
        setType("By Rev Center");
      }
      setLocalFilter(type);
    }
  };

  useEffect(() => {
    setPageNumber(0);
    handleLocalFilters();
  }, [property, store, revenueCenter, date, localCogsFilter]);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const result = await getApiResponse(url);
        setIsLoading(false);
        if (result?.data !== null) {
          setRowsData(result?.data);
        } else {
          showErrorToastSetState({
            errorMessage: result?.error || "",
            errorTitle: message.ERROR_MESSAGE_DASHBOARD,
            open: true,
          });
        }
      } catch (err) {
        showErrorToastSetState({
          errorMessage: "",
          errorTitle: message.ERROR_MESSAGE_DASHBOARD,
          open: true,
        });
        setIsLoading(false);
      }
    };
    if (filterType !== undefined) {
      getData();
    } else {
      setIsLoading(false);
    }
  }, [url, filterType]);

  let showDataTable;
  if (!revenueCenter) showDataTable = true;
  if (property === "002" && store) showDataTable = false;

  return (
    <Box>
      {showDataTable && (
        <DataTable
          sortHandler={sortHandler}
          title="COGS Breakdown"
          rowData={refineData(rowData)}
          page={pageNumber}
          showPagination={pagination}
          sortBy={sortBy}
          handleNavigate={handleNavigate}
          handlePageChange={handleChangePage}
          handleTypeChange={handleTypeChange}
          type={type}
          isLoading={isLoading}
          dyColumns={dyColumns}
          dropDownOptions={typeOptions}
        />
      )}
    </Box>
  );
};

export default CogsLocationBreakdownTable;
